import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Chip,
  Stack,
  IconButton,
  Breadcrumbs,
  Link,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { api } from '../services/api';
import { firmService, Firm } from '../services/firmService';

interface Row {
  id: number;
  username: string;
  full_name: string;
  agreement_title: string;
  agreement_version: number;
  signed_at: string;
  has_pdf: boolean;
}

const FirmLegal: React.FC = () => {
  const { firmId } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [firm, setFirm] = useState<Firm | null>(null);

  const load = async () => {
    if (!firmId) return;
    setLoading(true);
    try {
      const [agreementsRes, firmRes] = await Promise.all([
        api.get(`/firms/${firmId}/legal/agreements`),
        firmService.getFirmById(parseInt(firmId)),
      ]);
      setRows(agreementsRes.data.data || []);
      setFirm(firmRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmId]);

  const handleDownload = async (id: number) => {
    try {
      const res = await api.get(`/agreements/download/${id}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = res.headers['content-disposition'] as string | undefined;
      let filename = 'agreement.pdf';
      if (disposition) {
        const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition);
        const name = match?.[1] || match?.[2];
        if (name) filename = decodeURIComponent(name);
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // Optionally show a toast
    }
  };

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/firms');
          }}
          sx={{ textDecoration: 'none' }}
        >
          Firms
        </Link>
        {firm && (
          <Link
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate(`/firms/${firmId}`);
            }}
            sx={{ textDecoration: 'none' }}
          >
            {firm.name}
          </Link>
        )}
        <Typography color="text.primary">Legal</Typography>
      </Breadcrumbs>

      <Card>
        <CardHeader title="Legal Section" subheader="Agreements signed by firm users" />
        <Divider />
        <CardContent>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Full Name</TableCell>
                  <TableCell>Agreement Type</TableCell>
                  <TableCell>Acceptance Date</TableCell>
                  <TableCell align="right">Download</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.username}</TableCell>
                    <TableCell>{r.full_name}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{r.agreement_title}</span>
                        <Chip size="small" label={`v${r.agreement_version}`} />
                      </Stack>
                    </TableCell>
                    <TableCell>{new Date(r.signed_at).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <IconButton disabled={!r.has_pdf} onClick={() => handleDownload(r.id)} title="Download PDF">
                        <DownloadIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default FirmLegal;
